import React, { useMemo, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { Icon } from '../components/Icon/index'
import { ContentContainer } from '../components/ContentContainer'
import { useCountdown } from './useCountdown'
import { BOX_SHADOW } from '../styles/mixins/constants'

type Duration = number | 'indefinite' | undefined

type Position = 'top' | 'top-right' | 'left' | 'right'

type PublicParams<T = undefined> = {
    children?: JSX.Element | string
    position?: Position
    renderChild?: (props: {
        data: T | undefined
        isVisible: boolean
        durationMs?: Duration
        onClose: () => void
    }) => JSX.Element
    onClose?: () => void
}

type Props = {
    children: JSX.Element | string
    position: Position
    timeoutMs?: number
    onClose?: () => void
}

const Notification = (props: Props): JSX.Element => {
    const { children, timeoutMs, position, onClose } = props
    const { cancel, start } = useCountdown()
    useEffect(() => {
        if (timeoutMs) {
            cancel()
            start(timeoutMs, () => {
                onClose?.()
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeoutMs, onClose])
    return (
        <Container position={position}>
            <>
                {onClose ? (
                    <Header>
                        <Icon iconKey="Ex" size={3} color="secondary" onClick={onClose} />
                    </Header>
                ) : null}
                {children}
            </>
        </Container>
    )
}

const Header = styled.div`
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
`

const TOP = css`
    top: 0;
    left: 50%;
    transform: translateX(-50%);
`

const LEFT = css`
    top: 50%;
    left: 0;
    transform: translateY(-50%);
`

const RIGHT = css`
    top: 50%;
    right: 0;
    transform: translateY(-50%);
`

const TOP_RIGHT = css`
    top: 0;
    right: 0;
`

const Container = styled(ContentContainer)`
    display: flex;
    flex-flow: column nowrap;
    flex: 1 1 auto;
    position: fixed;
    z-index: 10001;
    ${BOX_SHADOW}
    ${(p: { position: Position }) => {
        switch (p.position) {
            case 'top':
                return TOP
            case 'left':
                return LEFT
            case 'right':
                return RIGHT
            case 'top-right':
                return TOP_RIGHT
            default:
                return ''
        }
    }}
`

type TriggerFn<T = undefined> = (params?: { isVisible: boolean; data?: T }) => void

export const useNotification = <T,>(params?: {
    durationMs?: Duration
}): {
    Component: (props: PublicParams<T>) => JSX.Element
    trigger: TriggerFn<T>
    updateNotificationProps: (props: T) => void
    isVisible: boolean
} => {
    const { durationMs } = params || {}
    const [isVisible, setVisible] = useState(false)
    const [componentProps, setComponentProps] = useState<T | undefined>()
    const Component = useMemo(() => {
        return ({ children, position = 'top', renderChild, onClose }: PublicParams<T>) => (
            <div>
                {isVisible ? (
                    <Notification
                        position={position}
                        timeoutMs={durationMs === 'indefinite' ? undefined : durationMs}
                        onClose={
                            onClose
                                ? () => {
                                      setVisible(false)
                                      setComponentProps(undefined)
                                      onClose?.()
                                  }
                                : undefined
                        }
                    >
                        {typeof renderChild === 'function'
                            ? renderChild({
                                  isVisible,
                                  data: componentProps,
                                  durationMs,
                                  onClose: () => {
                                      setVisible(false)
                                      onClose?.()
                                  },
                              })
                            : children || <div />}
                    </Notification>
                ) : (
                    <div />
                )}
            </div>
        )
    }, [durationMs, componentProps, isVisible])

    return {
        Component,
        trigger: (props) => {
            setComponentProps(props?.data)
            setVisible(Boolean(props?.isVisible))
        },
        updateNotificationProps: (props: T) => {
            setComponentProps(props)
        },
        isVisible,
    }
}

/**
 * 
 * (
      <div>
       <Notification
            timeoutMs={durationMs === 'indefinite' ? undefined : durationMs}
            onClose={() => {
              setVisible(false)
              onClose?.()
            }}
          >
            {children}
          </Notification>
      </div>
    )
 */
